import { Button, useDisclosure } from '@chakra-ui/react'
import type { AxiosError } from 'axios'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import type { InsuranceParamsPropertyDto, Step2Property } from 'BrokerTsLib/src/dtos'
import type { StepBase } from 'BrokerTsLib/src/dtos/StepBase'
import { Product } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { IndividualDiscountsSchema } from 'BrokerTsLib/src/validation'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { useApp } from '@/hooks/useApp'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import ErrorSummary from '../../components/ErrorSummary'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { FormSpacer } from '../../components/FormSpacer'
import { MainContent } from '../../components/MainContent'
import { useSaveStep2, useUpdateLastWizardStep } from '../../hooks/useWizard'
import { Save } from '../../icons'
import { useInsuranceParamsContext } from '../../layouts/Wizard'
import { Discounts } from '../discounts/Discounts'
import { ExtraInsuranceProperty } from '../ExtraInsuranceProperty'
import { FormButtons } from '../FormButtons'


export type Step2FormValues = Step2Property & StepBase

const schema = yup.object().shape(
  {
    individualDiscounts: IndividualDiscountsSchema
  }
)

export const Step2Form = () => {
  const navigate = useNavigate()
  const { product } = useApp()
  const { insuranceParams, insuranceParamsId: paramsId, refetchInsuranceParams } = useInsuranceParamsContext()
  const [isSubmittingCurrent, setIsSubmittingCurrent] = useState(false)
  const [isSubmittingNext, setIsSubmittingNext] = useState(false)
  const [discountsLoaded, setDiscountsLoaded] = useState(false)

  const { mutateAsync: updateStep } = useUpdateLastWizardStep()
  const { mutateAsync: submitForm, isError: isSubmitError } = useSaveStep2()
  const isContracted = !!insuranceParams?.generalParams.contractNumber

  const methods = useForm<Step2FormValues>({
    resolver: yupResolver(schema) as any, // aby se pripadne resetovaly serverove validace
    mode: 'onBlur',
    defaultValues: {
      property: (insuranceParams as InsuranceParamsPropertyDto).property
    },
  })

  const {
    handleSubmit,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = methods

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingCurrent(false)
      setIsSubmittingNext(false)
    }
  }, [isSubmitting])

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  const handlePostError = (data: any) => {
    Object.keys(data).forEach((x: any) => {
      if (x.toString().toLowerCase().startsWith('discounts[')) {
        setError(x.toLowerCase(), { message: data[x].join(' ') })
      }
      else {
        setError(global.toCamelCase(x), { message: data[x].join(' ') })
      }
    })
  }

  const onSubmitForm = handleSubmit((data) => {
    const values = _.cloneDeep(data)

    // Datum poslu jen jako date bez casu. To by chtelo vyresit asi nejak obecne. Kdyz se to posle i s casem, tak dochazi k casovemu posunu, coz znamena na serveru predchozi den
    if (values.property?.data?.newHomeAcquiryDate) {
      values.property.data.newHomeAcquiryDate = formatDate(values.property.data.newHomeAcquiryDate)
    }
    if (values.property?.data?.concurrentHouseholdInsuranceValidTo) {
      values.property.data.concurrentHouseholdInsuranceValidTo = formatDate(values.property.data.concurrentHouseholdInsuranceValidTo)
    }
    if (values.property?.data?.concurrentRealtyInsuranceValidTo) {
      values.property.data.concurrentRealtyInsuranceValidTo = formatDate(values.property.data.concurrentRealtyInsuranceValidTo)
    }
    if (values.property?.data?.realty?.startConstructionDate) {
      values.property.data.realty.startConstructionDate = formatDate(values.property.data.realty.startConstructionDate)
    }
    if (values.property?.data?.realty?.proposedEndConstructionDate) {
      values.property.data.realty.proposedEndConstructionDate = formatDate(values.property.data.realty.proposedEndConstructionDate)
    }

    return submitForm({ ...values, paramsId: paramsId ?? '' })
      .then((response) => {
        if (values.nextPage == 'next') {
          if ((insuranceParams?.generalParams.lastWizardStep ?? 2) < 3) {
            updateStep({ paramsId: Number(paramsId), step: 3, stepsStep: 'carkulka' }).then(() => {
              refetchInsuranceParams().then(() => {
                navigate(`/chaloupka/wizard/3/${paramsId}`)
              })
            })
          }
          else {
            navigate(`/chaloupka/wizard/3/${paramsId}`)
          }
        }
        else {
          savedFormAlertOnOpen()
        }
      })
      .catch((err: AxiosError) => {
        if (!(err.response && err.response.status === 400 && err.response.data)) {
          savedFormAlertOnOpen()
          return
        }

        const data = err.response.data
        handlePostError(data)
      })
      .finally(() => { })
  })

  const title = product == Product.LiabilityInsurance ? 'Pojištění' : 'Připojištění'

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmitForm}>
        <MainContent>
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>{title}</FormSectionTitle>

              <FormSectionDescription>
                Vyberte limity zvoleného {title.toLocaleLowerCase()}.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <ExtraInsuranceProperty />
            </FormSectionBody>
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Slevy pojištění</FormSectionTitle>

              <FormSectionDescription>
                Nastavte slevy pojistníka a jednotlivých pojišťoven.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <Discounts onDiscountsLoaded={() => setDiscountsLoaded(true)} />
            </FormSectionBody>
          </FormSection>

          {!isContracted && (
            <FormButtons
              rightButtons={
                <>

                  <Button variant="outline"
                    isDisabled={isSubmitting || isSubmittingNext || isSubmittingCurrent || !discountsLoaded}
                    isLoading={isSubmittingCurrent}
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'current')
                      setIsSubmittingCurrent(true)
                      onSubmitForm()
                    }}>
                    Uložit
                  </Button>

                  <Button
                    colorScheme="primary"
                    isLoading={isSubmittingNext}
                    isDisabled={isSubmitting || isSubmittingNext || isSubmittingCurrent || !discountsLoaded}
                    loadingText="Počítám nabídky..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'next')
                      setIsSubmittingNext(true)
                      onSubmitForm()
                    }}>
                    <Save />
                    Uložit a spočítat nabídky
                  </Button>

                  <SubmitDialog
                    isOpen={isSavedFormAlertOpen}
                    onClose={savedFormAlertOnClose}
                    colorScheme={!isSubmitError ? 'primary' : 'red'}
                    header={!isSubmitError ? 'Uloženo' : 'Chyba'}
                    body={!isSubmitError ? 'Parametry nabídky byly úspěšně uloženy' : 'Při ukládání došlo k chybě'}
                  />
                </>
              }
            />
          )}
        </MainContent>

        <FormSpacer />

        <ErrorSummary errors={errors} />
      </form>
    </FormProvider>
  )
}
